import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import axiosInstance from "@/api/axios";
import { useBagApi } from "@/api/bag-query";
import DiscoveryFooter from "@/components/index/discovery-footer";
import NavbarMinimal from "@/components/index/navbar-minimal";
import { Button } from "@/components/ui/button";
import BuyNowConfirmationModal from "@/components/ui/buy-now-confirmation-modal";
import { DownloadAppModal } from "@/components/ui/download-app-modal";
import LoginPromptModalDiscovery from "@/components/ui/login-prompt-modal-discovery";
import { Skeleton } from "@/components/ui/skeleton";
import { stripCategorySuffix } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { useBagStore } from "@/store/bag";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";

function ProductDetail() {
	const addToGuestBag = useBagStore(state => state.addItem);
	const { addToBag: addToAuthBag, getCartBags, updateBagItem } = useBagApi();

	const isAuthenticated = useAuthStore(state => state.isAuthenticated);
	const [loadingBuyNow, setLoadingBuyNow] = useState(false);
	const [showBuyNowModal, setShowBuyNowModal] = useState(false);
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const [buyNowBagId, setBuyNowBagId] = useState<string>("");
	const [buyNowBagItemId, setBuyNowBagItemId] = useState<string>("");
	const navigate = useNavigate();

	const [showGallery, setShowGallery] = useState(false);
	const [galleryStartIdx, setGalleryStartIdx] = useState(0);
	const { productid } = useParams({ strict: false });
	const [product, setProduct] = useState<any>(null);
	const [activeImageIdx, setActiveImageIdx] = useState(0);
	const [showLoginPrompt, setShowLoginPrompt] = useState(false);
	const [pendingIntent, setPendingIntent] = useState<"buyNow" | "negotiate" | null>(null);

	// Helper to normalize vendorId from product object
	// Always return the vendor's unique ID (never name)
	const getVendorId = (p: any) => {
		if (typeof p.vendorId === "object" && p.vendorId?.id)
			return p.vendorId.id;
		if (typeof p.vendorId === "string")
			return p.vendorId;
		if (p.vendor && p.vendor.id)
			return p.vendor.id;
		return undefined;
	};

	useEffect(() => {
		if (!productid)
			return;
		fetchProductById(productid).then(setProduct);
	}, [productid]);

	// Resume pending intent after login redirect
	const intentHandled = useRef(false);
	useEffect(() => {
		if (!isAuthenticated || !product || intentHandled.current)
			return;
		const intent = window.sessionStorage.getItem("pendingProductIntent");
		if (!intent)
			return;
		intentHandled.current = true;
		window.sessionStorage.removeItem("pendingProductIntent");
		// Defer to next tick so handlers are ready
		const timerId = setTimeout(() => {
			if (intent === "buyNow") {
				handleBuyNow();
			}
			else if (intent === "negotiate") {
				handleNegotiate();
			}
		}, 0);
		return () => clearTimeout(timerId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, product]);

	// Fetch product by ID (moved here for scope)
	async function fetchProductById(id: string) {
		const res = await axios.get(`${import.meta.env.VITE_BASE_URL}/product/${id}/details`);
		const data = res.data?.data || {};
		return {
			...data,
			id: data.id,
			name: data.name,
			price: data.price,
			vendor: data.vendorId,
			vendorId: data.vendorId,
			images: data.photos || [],
			color: data.color || "-",
			sizes: data.sizes || [],
			ratings: data.ratings ?? null,
			reviews: [],
			moreProducts: []
		};
	}

	const [allVendorProducts, setAllVendorProducts] = useState<any[]>([]);

	// Fetch ALL vendor products once (no category/search filtering in API)
	useEffect(() => {
		if (!product)
			return;
		const vendorId = getVendorId(product);
		if (!vendorId)
			return;
		const fetchVendorProducts = async () => {
			try {
				const params: Record<string, any> = {
					vendorId: String(vendorId),
					limit: 100
				};
				const res = await axiosInstance.get(`/product/discovery`, { params });
				const items = res.data?.data?.items || [];
				// Safety: client-side filter to ensure only this vendor's products
				const vid = String(vendorId);
				const vendorFiltered = items.filter((p: any) => {
					const pVid = getVendorId(p);
					return pVid && String(pVid) === vid;
				});
				setAllVendorProducts(vendorFiltered);
			}
			catch (err) {
				console.error("Failed to fetch vendor products:", err);
				setAllVendorProducts([]);
			}
		};
		fetchVendorProducts();
	}, [product]);

	useEffect(() => {
		setActiveImageIdx(0); // Reset to first image when product changes
	}, [product]);

	const handleLoginRedirect = () => {
		if (typeof window !== "undefined") {
			// Redirect back to this product page after login
			window.sessionStorage.setItem("afterLoginRedirect", `/product/${productid}`);
			// Save the intent so we can resume after login
			if (pendingIntent) {
				window.sessionStorage.setItem("pendingProductIntent", pendingIntent);
			}
			window.location.href = "/sign-in";
		}
	};

	async function handleBuyNow() {
		if (!isAuthenticated) {
			setPendingIntent("buyNow");
			setShowLoginPrompt(true);
			return;
		}
		// Show modal immediately with skeleton loading
		setShowBuyNowModal(true);
		setLoadingBuyNow(true);
		try {
			const vendorId = typeof product.vendor === "object" && product.vendor?.id
				? product.vendor.id
				: typeof product.vendorId === "string"
					? product.vendorId
					: "";

			// Step 1: Check if product already in cart
			const cartData = await getCartBags();
			let foundBagId = "";
			let foundBagItemId = "";

			if (cartData?.cartBags) {
				for (const bag of cartData.cartBags) {
					const productInBag = bag.items?.find((item: any) =>
						item.productId === product.id || item.id === product.id
					);
					if (productInBag) {
						foundBagId = bag.id;
						foundBagItemId = productInBag.bagItemId || productInBag.id;
						break;
					}
				}
			}

			// Step 2: If not in bag, add it
			if (!foundBagId) {
				await addToAuthBag({
					productId: product.id,
					vendorId,
					quantity: 1,
					isWishlist: false
				});
				// Get bag ID from response or fetch cart again
				const updatedCart = await getCartBags();
				for (const bag of updatedCart.cartBags || []) {
					const productInBag = bag.items?.find((item: any) =>
						item.productId === product.id || item.id === product.id
					);
					if (productInBag) {
						foundBagId = bag.id;
						foundBagItemId = productInBag.bagItemId || productInBag.id;
						break;
					}
				}
			}

			// Step 3: Set bag IDs for checkout
			if (foundBagId && foundBagItemId) {
				setBuyNowBagId(foundBagId);
				setBuyNowBagItemId(foundBagItemId);
			}
			else {
				setShowBuyNowModal(false);
				toast.error("Failed to prepare checkout");
			}
		}
		catch (err) {
			console.error("Buy now error:", err);
			setShowBuyNowModal(false);
			toast.error("Failed to prepare checkout");
		}
		finally {
			setLoadingBuyNow(false);
		}
	}

	// Negotiate - disabled on web, redirect to download mobile app
	// async function handleNegotiate() {
	// 	if (!isAuthenticated) {
	// 		setPendingIntent("negotiate");
	// 		setShowLoginPrompt(true);
	// 		return;
	// 	}
	// 	setLoadingNegotiate(true);
	// 	try {
	// 		const res = await axiosInstance.post("/v1/bag/buy-now", {
	// 			productId: product.id,
	// 			quantity: 1
	// 		});
	// 		const bagId = res.data?.data?.bagId || res.data?.data?.id;
	// 		if (bagId) {
	// 			await axiosInstance.post(`/v1/bag/negotiate/${bagId}`);
	// 			navigate({ to: `/bag` });
	// 		}
	// 	} catch (err) {
	// 		console.error(err);
	// 	} finally {
	// 		setLoadingNegotiate(false);
	// 	}
	// }
	function handleNegotiate() {
		setShowDownloadModal(true);
	}

	// Helper to build BagItem from product

	const handleAddToBag = async () => {
		if (!product)
			return;

		const vendorId = typeof product.vendor === "object" && product.vendor?.id
			? product.vendor.id
			: typeof product.vendorId === "string"
				? product.vendorId
				: "";

		if (isAuthenticated) {
			// For authenticated users, call the API
			try {
				await addToAuthBag({
					productId: product.id,
					vendorId,
					quantity: 1,
					isWishlist: false
				});
				toast.success("Added to bag!");
			}
			catch (err) {
				console.error("Failed to add to bag:", err);
				toast.error("Failed to add to bag");
			}
		}
		else {
			// For guest users, use local store
			addToGuestBag({
				id: product.id,
				vendorId,
				vendorName: product.vendor?.fullName || product.vendor?.name || "Vendor",
				images: product.images || [],
				pricePerCard: Number(product.price),
				grandTotal: Number(product.price),
				quantity: 1,
				productName: stripCategorySuffix(product.name) || product.title || undefined
			});
			toast.success("Added to bag!");
		}
	};

	if (!product) {
		return (
			<div className="bg-gray-50 min-h-screen flex flex-col">
				<NavbarMinimal />

				<LoginPromptModalDiscovery
					open={showLoginPrompt}
					onClose={() => setShowLoginPrompt(false)}
					onLogin={handleLoginRedirect}
				/>
				<div className="flex-1">
					{/* Back button skeleton */}
					<div className="flex items-center mb-2">
						<div className="rounded-full p-1" style={{ marginLeft: 12, marginTop: 12 }}>
							<Skeleton className="w-6 h-6 rounded-full" />
						</div>
					</div>
					<div className="max-w-7xl mx-auto w-full px-4 pt-8 pb-16">
						<div className="flex flex-col lg:flex-row gap-8 w-full">
							{/* Left: Image Skeleton */}
							<div className="flex flex-row w-full lg:w-auto" style={{ gap: 20 }}>
								{/* Thumbnails skeleton */}
								<div className="flex flex-col items-center gap-3 h-full justify-start" style={{ width: "clamp(60px, 8vw, 110px)", minWidth: 60, maxWidth: 110 }}>
									{[1, 2, 3, 4].map(i => (
										<Skeleton key={i} className="w-full aspect-square rounded-lg" style={{ minHeight: 60, maxHeight: 110, height: "clamp(60px, 8vw, 110px)" }} />
									))}
								</div>
								{/* Main Image skeleton */}
								<div className="flex-1 flex items-center justify-center min-w-0">
									<Skeleton className="w-full max-w-[400px] h-[60vw] md:h-[500px] rounded-lg" />
								</div>
							</div>
							{/* Right: Details Skeleton */}
							<div className="flex-1 flex flex-col gap-4 w-full max-w-xl mx-auto">
								<Skeleton className="h-10 w-32" />
								<Skeleton className="h-6 w-3/4" />
								<Skeleton className="h-20 w-full" />
								<div className="flex flex-col sm:flex-row gap-2 mt-4 justify-between items-center w-full">
									<div className="flex gap-2 w-full sm:w-auto">
										<Skeleton className="h-10 w-28 rounded-full" />
										<Skeleton className="h-10 w-28 rounded-full" />
									</div>
									<Skeleton className="h-10 w-48 rounded-full" />
								</div>
							</div>
						</div>
						{/* More Products Skeleton */}
						<div className="mt-12">
							<Skeleton className="h-6 w-48 mb-4" />
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								{[1, 2, 3, 4].map(i => (
									<div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
										<Skeleton className="w-full aspect-square" />
										<div className="p-3 flex flex-col gap-1">
											<Skeleton className="h-3 w-16" />
											<Skeleton className="h-4 w-24" />
											<Skeleton className="h-4 w-20" />
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
				<DiscoveryFooter />
			</div>
		);
	}

	return (
		<div className="bg-gray-50 min-h-screen flex flex-col">
			{/* Shared Navbar and Delivery Guarantee */}
			<NavbarMinimal />
			{/* Login Modal using reusable component */}
			<LoginPromptModalDiscovery
				open={showLoginPrompt}
				onClose={() => setShowLoginPrompt(false)}
				onLogin={handleLoginRedirect}
			/>
			{/* Buy Now Confirmation Modal */}
			<BuyNowConfirmationModal
				open={showBuyNowModal}
				loading={loadingBuyNow}
				onClose={() => setShowBuyNowModal(false)}
				onCheckout={async (quantity: number) => {
					try {
						// Update bag item quantity if different from 1
						if (quantity !== 1 && buyNowBagItemId) {
							await updateBagItem(buyNowBagId, buyNowBagItemId, { quantity });
						}
						setShowBuyNowModal(false);
						navigate({ to: `/checkout/shipping`, search: { bagId: buyNowBagId } });
					}
					catch (error) {
						console.error("Failed to update quantity:", error);
						toast.error("Failed to update quantity");
					}
				}}
				product={{
					id: product?.id || "",
					name: stripCategorySuffix(product?.name) || "",
					price: product?.price || 0,
					images: product?.images || [],
					vendorName: product?.vendor?.fullName || product?.vendor?.name || "Vendor",
					quantity: 1
				}}
				bagId={buyNowBagId}
			/>
			<div className="flex-1">
				{/* Back button: returns to previous page (matches bag page) */}
				<div className="flex items-center mb-2">
					<button
						type="button"
						aria-label="Go back"
						className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
						style={{ position: "relative", lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, marginLeft: 12, marginTop: 12 }}
						onClick={() => window.history.back()}
					>
						<img
							src="/assets/icons/arrow-left.svg"
							alt="Back"
							style={{ width: 24, height: 24, display: "block" }}
						/>
					</button>
				</div>
				<div className="max-w-7xl mx-auto w-full px-4 pt-8 pb-16">
					<div className="flex flex-col lg:flex-row gap-8 w-full">
						{/* Left: Images (thumbnails + main image in a row) */}
						<div
							className="flex flex-row w-full lg:w-auto"
							style={{ gap: 20, opacity: 1, transform: "rotate(0deg)" }}
						>
							{/* Thumbnails vertical */}
							<div
								className="flex flex-col items-center gap-3 h-full justify-start"
								style={{
									width: "clamp(60px, 8vw, 110px)",
									minWidth: 60,
									maxWidth: 110,
									opacity: 1,
									transform: "rotate(0deg)",
									height: "100%"
								}}
							>
								{product.images.slice(0, 4).map((img: string, idx: number) => {
									const isLastThumb = idx === 3 && product.images.length > 4;
									if (isLastThumb) {
										const extraCount = product.images.length - 4;
										return (
											<div
												key={idx}
												tabIndex={0}
												role="button"
												aria-label={`Show all images, plus ${extraCount} more`}
												className="transition-all duration-150 hover:scale-105"
												style={{
													width: "100%",
													aspectRatio: "1/1",
													borderRadius: 10,
													border: activeImageIdx === 4 ? "2px solid #3F2EC8" : "2px solid transparent",
													boxSizing: "border-box",
													position: "relative",
													overflow: "hidden",
													cursor: "pointer",
													minHeight: 60,
													maxHeight: 110,
													height: "clamp(60px, 8vw, 110px)"
												}}
												onClick={() => {
													setShowGallery(true);
													setGalleryStartIdx(3);
												}}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														setShowGallery(true);
														setGalleryStartIdx(3);
													}
												}}
											>
												<img
													src={img}
													alt={`thumb-${idx}`}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
														borderRadius: 10,
														filter: "brightness(0.7)"
													}}
												/>
												<div
													style={{
														position: "absolute",
														top: 0,
														left: 0,
														width: "100%",
														height: "100%",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														color: "#FFFFFF",
														fontFamily: "Inter, sans-serif",
														fontWeight: 700,
														fontStyle: "bold",
														fontSize: 24,
														lineHeight: "140%",
														letterSpacing: 0,
														verticalAlign: "middle",
														background: "rgba(0,0,0,0.3)",
														borderRadius: 10
													}}
												>
													+
													{extraCount}
												</div>
											</div>
										);
									}
									return (
										<img
											key={idx}
											src={img}
											alt={`thumb-${idx}`}
											className="object-cover cursor-pointer transition-all duration-150 hover:scale-105"
											tabIndex={0}
											role="button"
											aria-label={`Show image ${idx + 1}`}
											style={{
												width: "100%",
												aspectRatio: "1/1",
												borderRadius: 10,
												border: activeImageIdx === idx ? "2px solid #3F2EC8" : "2px solid transparent",
												boxSizing: "border-box",
												minHeight: 60,
												maxHeight: 110,
												height: "clamp(60px, 8vw, 110px)"
											}}
											onClick={() => setActiveImageIdx(idx)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													setActiveImageIdx(idx);
												}
											}}
										/>
									);
								})}
							</div>
							{/* Main Image */}
							<div
								className="flex-1 flex items-center justify-center min-w-0"
								style={{
									width: "100%",
									height: "auto",
									opacity: 1,
									transform: "rotate(0deg)",
									zIndex: showGallery ? 100 : 1,
									position: "relative"
								}}
							>
								<img
									src={product.images[activeImageIdx]}
									alt={product.name}
									className="w-full max-w-[400px] h-auto max-h-[60vw] md:max-h-[500px] object-cover rounded-lg border border-gray-200 cursor-pointer transition-all duration-150 hover:scale-[1.02] hover:shadow-lg"
									tabIndex={0}
									role="button"
									aria-label="Open image gallery"
									onClick={() => {
										setShowGallery(true);
										setGalleryStartIdx(activeImageIdx);
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											setShowGallery(true);
											setGalleryStartIdx(activeImageIdx);
										}
									}}
								/>
							</div>
						</div>
						{/* Right: Details */}
						<div className="flex-1 flex flex-col gap-4 w-full max-w-xl mx-auto">
							<div
								className="text-3xl md:text-4xl font-semibold text-[#3F2EC8]"
							>
								₦
								{product.price.toLocaleString()}
							</div>
							<div
								className="flex items-center justify-between w-full"
							>
								<h2
									className="font-medium text-lg md:text-xl text-[#2A2A2A] m-0"
									style={{ margin: 0 }}
								>
									{product.name}
								</h2>
								{/* Rating display (if needed) */}
							</div>

							{/* Product Description */}
							{product.description && (
								<div className="text-base text-[#444] mt-2 mb-2 leading-6">
									{product.description}
								</div>
							)}
							{/* <div className="text-gray-700">
								Color:
								{product.color}
							</div>
							<div className="flex flex-wrap gap-2 mt-2">
								{product.sizes.map((size: string) => (
									<span key={size} className="px-2 py-1 border rounded text-xs">{size}</span>
								))}
							</div> */}
							{/* <div className="flex items-center gap-2 mt-4">
								<span>Quantity</span>
								<button className="px-2 py-1 border rounded">-</button>
								<span>1</span>
								<button className="px-2 py-1 border rounded">+</button>
							</div> */}
							<div className="flex flex-col sm:flex-row gap-2 mt-4 justify-between items-center w-full">
								<div className="flex gap-2 w-full sm:w-auto">
									<button
										className="bg-[#3F2EC8] text-white px-4 py-2 rounded-full transition-colors duration-150 hover:bg-[#2A1EA6] hover:shadow-md w-full sm:w-auto"
										onClick={handleBuyNow}
										disabled={loadingBuyNow}
									>
										{loadingBuyNow ? "Processing..." : "Buy now"}
									</button>
									<button
										className="border border-[#3F2EC8] text-[#3F2EC8] px-4 py-2 rounded-full transition-colors duration-150 hover:bg-[#3F2EC8] hover:text-white hover:shadow-md w-full sm:w-auto"
										onClick={handleAddToBag}
									>
										Add to bag
									</button>
								</div>
								<button
									className="border border-[#3F2EC8] text-[#3F2EC8] px-4 py-2 rounded-full transition-colors duration-150 hover:bg-[#3F2EC8] hover:text-white hover:shadow-md w-full sm:w-auto"
									onClick={handleNegotiate}
								>
									Negotiate with vendor
								</button>
							</div>
							<DownloadAppModal open={showDownloadModal} onOpenChange={setShowDownloadModal} />
						</div>
					</div>
					{/* More Products by Vendor */}
					<div className="mt-12">
						<div className="flex flex-wrap items-center gap-2 mb-4">
							<span className="text-lg font-semibold">More Products by Vendor</span>
						</div>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							{(() => {
								const filteredProducts = allVendorProducts.filter(
									p => p.id !== product.id
								);
								return filteredProducts.length > 0
									? (
											filteredProducts.map(p => (
												<Link
													key={p.id}
													to="/product/$productid"
													params={{ productid: p.id }}
													className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col cursor-pointer hover:shadow-lg transition-shadow duration-150"
												>
													<img src={p.photos?.[0] || "/assets/icons/shoe1.jpg"} alt="product" className="w-full aspect-square object-cover" />
													<div className="p-3 flex flex-col gap-1">
														<span className="text-xs text-blue-700 font-semibold">{p.vendorId?.fullName || product.vendor?.name}</span>
														<span className="text-sm text-gray-800">{stripCategorySuffix(p.name)}</span>
														<span className="text-sm font-bold text-gray-900">
															₦
															{p.price}
														</span>
													</div>
												</Link>
											))
										)
									: (
											<div className="col-span-full text-center text-gray-400">No products found.</div>
										);
							})()}
						</div>
					</div>
				</div>
			</div>
			<DiscoveryFooter />
			{/* Gallery/Lightbox Modal for all images (styled like login prompt modal) */}
			{showGallery && (
				<GalleryModal
					open={showGallery}
					onClose={() => setShowGallery(false)}
					images={product.images}
					activeIdx={galleryStartIdx}
					setActiveIdx={setGalleryStartIdx}
				/>
			)}
		</div>
	);
}
function GalleryModal({ open, onClose, images, activeIdx, setActiveIdx }: { open: boolean; onClose: () => void; images: string[]; activeIdx: number; setActiveIdx: (idx: number) => void }) {
	const modalRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (open) {
			const originalOverflow = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			const appContent = document.getElementById("app-content");
			if (appContent)
				appContent.setAttribute("aria-hidden", "true");
			if (modalRef.current)
				modalRef.current.focus();
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === "ArrowLeft") {
					setActiveIdx(activeIdx > 0 ? activeIdx - 1 : images.length - 1);
				}
				else if (e.key === "ArrowRight") {
					setActiveIdx(activeIdx < images.length - 1 ? activeIdx + 1 : 0);
				}
				else if (e.key === "Tab" && modalRef.current) {
					const focusable = modalRef.current.querySelectorAll<HTMLElement>(
						"button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
					);
					const first = focusable[0];
					const last = focusable[focusable.length - 1];
					if (!focusable.length)
						return;
					if (e.shiftKey) {
						if (document.activeElement === first) {
							e.preventDefault();
							last.focus();
						}
					}
					else {
						if (document.activeElement === last) {
							e.preventDefault();
							first.focus();
						}
					}
				}
			};
			document.addEventListener("keydown", handleKeyDown);
			return () => {
				document.body.style.overflow = originalOverflow;
				if (appContent)
					appContent.removeAttribute("aria-hidden");
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [open, activeIdx, images.length, setActiveIdx]);
	if (!open)
		return null;
	return (
		<div
			ref={modalRef}
			className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 animate-fadeIn"
			onClick={onClose}
			tabIndex={-1}
			onKeyDown={(e) => {
				if (e.key === "Escape")
					onClose();
			}}
		>
			{/* Close button */}
			<button
				className="absolute top-4 right-4 z-20 rounded-full p-2 hover:bg-white/20 transition-colors duration-150"
				aria-label="Close fullscreen gallery"
				onClick={onClose}
			>
				<img
					src="/assets/icons/close.svg"
					alt="Close"
					style={{ width: 24, height: 24, display: "block", filter: "brightness(0) invert(1)" }}
				/>
			</button>

			{/* Image counter */}
			<div className="absolute top-4 left-4 z-20 text-white/80 text-sm font-medium bg-black/40 rounded-full px-3 py-1">
				{activeIdx + 1}
				{" "}
				/
				{images.length}
			</div>

			{/* Main image area */}
			<div className="flex-1 flex items-center justify-center w-full px-12 sm:px-16 py-4" onClick={e => e.stopPropagation()}>
				{/* Previous arrow */}
				{images.length > 1 && (
					<button
						className="absolute left-2 sm:left-4 z-20 rounded-full p-2 hover:bg-white/20 transition-colors duration-150"
						aria-label="Previous image"
						onClick={() => setActiveIdx(activeIdx > 0 ? activeIdx - 1 : images.length - 1)}
					>
						<img src="/assets/icons/arrow-left.svg" alt="Previous" style={{ width: 24, height: 24, display: "block", filter: "brightness(0) invert(1)" }} />
					</button>
				)}

				<img
					src={images[activeIdx]}
					alt={`gallery-img-${activeIdx}`}
					className="max-w-full max-h-[80vh] object-contain select-none"
					style={{ borderRadius: 8 }}
					draggable={false}
				/>

				{/* Next arrow */}
				{images.length > 1 && (
					<button
						className="absolute right-2 sm:right-4 z-20 rounded-full p-2 hover:bg-white/20 transition-colors duration-150"
						aria-label="Next image"
						onClick={() => setActiveIdx(activeIdx < images.length - 1 ? activeIdx + 1 : 0)}
					>
						<img src="/assets/icons/arrow-right.svg" alt="Next" style={{ width: 24, height: 24, display: "block", filter: "brightness(0) invert(1)" }} />
					</button>
				)}
			</div>

			{/* Thumbnails at bottom */}
			{images.length > 1 && (
				<div className="flex gap-2 justify-center pb-6 pt-2 px-4" onClick={e => e.stopPropagation()}>
					{images.map((img, idx) => (
						<Button
							key={idx}
							variant="ghost"
							size="icon"
							aria-label={`Show image ${idx + 1}`}
							className={`p-0 h-auto w-auto rounded-lg transition-all duration-150 hover:bg-transparent ${
								activeIdx === idx ? "scale-105" : "scale-90 hover:scale-105"
							}`}
							onClick={() => setActiveIdx(idx)}
						>
							<img
								src={img}
								alt={`gallery-thumb-${idx}`}
								className="transition-all duration-150"
								style={{
									width: 56,
									height: 56,
									borderRadius: 8,
									objectFit: "cover",
									border: activeIdx === idx ? "2px solid #3F2EC8" : "2px solid transparent",
									opacity: activeIdx === idx ? 1 : 0.7
								}}
							/>
						</Button>
					))}
				</div>
			)}
		</div>
	);
}

export const Route = createFileRoute("/product/$productid")({
	component: ProductDetail
});
// React's useRef is already imported at the top, so this is not needed and should be removed.
// If you want to provide a minimal implementation for a non-React environment, you could do:
